'use client';

import { Pencil, UserCircle2, XIcon } from 'lucide-react';
import { usePathname } from 'next/navigation';
import posthog from 'posthog-js';
import { type ReactNode, useEffect, useState } from 'react';
import { cn } from '../../utils';
import { FormLabel } from '../Form';
import { ResponsiveImage } from '../image-input';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '../ui';

type ModalCardType = 'facility' | 'age-group' | 'staff';
export function ModalCard({
  imageFallback = <UserCircle2 className="h-full w-full text-gray-300" />,
  hideActions = false,
  size = 'medium',
  ...props
}: {
  type: ModalCardType;
  photoUrl: string | null;
  imageFallback?: ReactNode;
  cardTitle: string;
  cardSubtitle: string;
  modal: {
    title: string;
    description: string;
    bio: ReactNode;
  };
  containerClassName?: string;
  itemId: number;
  canEdit?: boolean;
  editForm?: (props: { id: number; onEditComplete: () => void }) => ReactNode;
  onCardX?: () => void;
  hideActions?: boolean;
  size?: 'small' | 'medium' | 'large';
}) {
  const [isEditing, setIsEditing] = useState<number | null>(null);

  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  useEffect(() => {
    posthog.capture('modal_card_opened', {
      type: props.type,
      item_id: props.itemId,
      pathname,
    });
  }, [open, props.type, props.itemId, pathname]);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-sm border border-gray-400',
        props.containerClassName
      )}
    >
      <div className="relative aspect-square overflow-hidden">
        {props.photoUrl ? (
          <ResponsiveImage
            iconUrl={props.photoUrl}
            imageClassName="rounded-none"
            containerClassName="h-max"
          />
        ) : (
          <div className="flex w-full items-center justify-center bg-gray-100 p-10">
            {imageFallback}
          </div>
        )}
      </div>
      {!!props.onCardX && (
        <button
          type="button"
          className="absolute top-2 right-2 z-20 rounded-full border bg-white p-1 text-black transition-colors hover:bg-gray-100"
          onClick={() => {
            props.onCardX?.();
          }}
        >
          <XIcon size={16} />
        </button>
      )}
      <div
        className={cn(
          'flex flex-col items-center justify-center bg-white p-3',
          size === 'small' && 'px-0'
        )}
      >
        <p
          className={cn(
            size === 'small' && 'max-w-full truncate text-base',
            'font-semibold text-gray-800',
            size === 'large' && 'text-2xl'
          )}
        >
          {props.cardTitle}
        </p>
        <p className={cn('text-gray-600 text-sm')}>{props.cardSubtitle}</p>
        <Dialog
          open={open}
          onOpenChange={(open) => {
            setOpen(open);
          }}
        >
          {!hideActions && (
            <div className="flex w-full space-x-1.5 lg:space-x-3">
              <DialogTrigger asChild>
                <Button
                  variant={'default'}
                  className="title mt-4 h-min w-full bg-red py-2!"
                  onClick={() => {
                    setIsEditing(null);
                  }}
                >
                  VIEW
                </Button>
              </DialogTrigger>
              {!!props.editForm && !!props.canEdit && (
                <Button
                  variant={'outline'}
                  className="mt-4 h-min w-min bg-gray-50 px-2.5 py-2! "
                  onClick={() => {
                    setIsEditing(props.itemId);
                    setOpen(true);
                  }}
                >
                  <Pencil size={20} />
                </Button>
              )}
            </div>
          )}
          <DialogContent>
            <DialogTitle className="text-xl">{props.modal.title}</DialogTitle>
            {isEditing ? (
              props.editForm?.({
                id: isEditing,
                onEditComplete: () => {
                  setIsEditing(null);
                  setOpen(false);
                },
              })
            ) : (
              <>
                <DialogDescription className="text-base">
                  {props.modal.description}
                </DialogDescription>
                {!!props.photoUrl && (
                  <div>
                    <FormLabel label="Photo" name="photo" errorMessage={''} />
                    <div className="w-64">
                      <ResponsiveImage iconUrl={props.photoUrl!} />
                    </div>
                  </div>
                )}
                <div>
                  <FormLabel label="Bio" name="bio" errorMessage={''} />
                  <p className="rounded-sm py-1 text-base text-gray-700">
                    {props.modal.bio}
                  </p>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
